import { Link } from "react-router-dom";
import { SEO } from "../components/common/SEO";
import { PortraitPlaceholder } from "../components/common/BrandVisuals";
import { CmsIcon } from "../components/common/CmsIcon";
import { QuickContactForm } from "../components/forms/QuickContactForm";
import { useLanguage } from "../context/LanguageProvider";
import { useSiteData } from "../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../lib/i18n";
import { whatsappHref } from "../lib/format";
import { ArrowRight, MapPin, Phone, MessageCircle } from "lucide-react";

export default function Home() {
    const { lang } = useLanguage();
    const { siteContent, practiceAreas, contactInfo, ready } = useSiteData();

    const home = siteContent?.home || {};
    const seo = siteContent?.seo?.home || {};

    return (
        <div data-testid="page-home">
            <SEO
                title={pick(seo.title, lang) ||
                    "Cabinet d'avocats Maître Mina Adja Bintou Sanou"}
                description={pick(seo.description, lang)}
                lang={lang}
            />

            {/* ---------- HERO ---------- */}
            <section
                className="relative overflow-hidden bg-ink noise-overlay"
                data-testid="home-hero"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 rise-in">
                        <div className="eyebrow mb-5">
                            <span className="gold-rule mr-3" />
                            {pick(
                                {
                                    fr: "Cabinet d'avocats · Bobo-Dioulasso",
                                    en: "Law firm · Bobo-Dioulasso",
                                },
                                lang,
                            )}
                        </div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-neutral-50">
                            {pick(home.hero_title, lang) ||
                                "Cabinet d'avocats Maître Mina Adja Bintou Sanou"}
                        </h1>
                        <p
                            className="mt-5 font-serif italic text-xl md:text-2xl text-gold/90 leading-snug max-w-2xl"
                            data-testid="hero-tagline"
                        >
                            {pick(home.hero_tagline, lang) ||
                                "Une expertise juridique internationale au service de vos droits"}
                        </p>
                        <p className="mt-6 max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed">
                            {pick(home.hero_subtitle, lang) ||
                                pick(UI_STRINGS.misc.dual_bar_short, lang)}
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4 rise-in rise-delay-2">
                            <Link
                                to="/contact#form"
                                className="btn-gold"
                                data-testid="hero-cta-book"
                            >
                                {pick(UI_STRINGS.cta.book, lang)}
                                <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/domaines"
                                className="btn-outline-gold"
                                data-testid="hero-cta-practice"
                            >
                                {pick(
                                    {
                                        fr: "Découvrir les domaines",
                                        en: "Explore practice areas",
                                    },
                                    lang,
                                )}
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-5 rise-in rise-delay-2">
                        <PortraitPlaceholder
                            src={siteContent?.images?.portrait_url || ""}
                            className="aspect-[3/4] w-full max-w-md mx-auto md:ml-auto object-cover"
                            alt={pick(
                                {
                                    fr: "Portrait de Maître Sanou",
                                    en: "Portrait of Maître Sanou",
                                },
                                lang,
                            )}
                        />
                    </div>
                </div>
            </section>

            {/* ---------- CREDIBILITY BANNER ---------- */}
            <section
                data-testid="credibility-banner"
                className="bg-marble border-y border-ink-border"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 md:py-14 relative z-10">
                    <div className="text-center">
                        <div className="eyebrow mb-3">
                            {pick(
                                {
                                    fr: "Double barreau actif",
                                    en: "Active dual bar membership",
                                },
                                lang,
                            )}
                        </div>
                        <p className="font-serif text-2xl md:text-3xl text-neutral-50 max-w-4xl mx-auto leading-tight">
                            {pick(home.credibility_banner, lang)}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-4 justify-center">
                            <span className="text-xs md:text-sm tracking-widest uppercase text-gold border border-gold px-4 py-2">
                                Barreau du Québec · 2020
                            </span>
                            <span className="text-xs md:text-sm tracking-widest uppercase text-gold border border-gold px-4 py-2">
                                {pick(
                                    {
                                        fr: "Barreau du Burkina Faso · 2022",
                                        en: "Bar of Burkina Faso · 2022",
                                    },
                                    lang,
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- PRACTICE TEASER ---------- */}
            <section
                id="practice"
                className="bg-ink py-20 md:py-28"
                data-testid="home-practice-teaser"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10">
                    <div className="max-w-2xl mb-14">
                        <div className="eyebrow mb-3">
                            {pick(UI_STRINGS.sections.practice_teaser, lang)}
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight text-neutral-50">
                            {pick(UI_STRINGS.sections.practice_teaser_sub, lang)}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {ready &&
                            practiceAreas.map((pa) => (
                                <Link
                                    key={pa.slug}
                                    to={`/domaines#${pa.slug}`}
                                    className="card-line p-6 group flex flex-col"
                                    data-testid={`home-practice-card-${pa.slug}`}
                                >
                                    <div className="mb-4">
                                        <CmsIcon name={pa.icon} size={26} />
                                    </div>
                                    <h3 className="font-serif text-xl text-neutral-50 leading-snug mb-3">
                                        {pick(pa.title, lang)}
                                    </h3>
                                    <p className="text-sm text-neutral-400 line-clamp-3 leading-relaxed flex-1">
                                        {pick(pa.description, lang)}
                                    </p>
                                    <div className="mt-4 text-xs uppercase tracking-widest text-gold flex items-center gap-2 group-hover:gap-3 transition-all">
                                        {pick(
                                            {
                                                fr: "En savoir plus",
                                                en: "Learn more",
                                            },
                                            lang,
                                        )}
                                        <ArrowRight size={14} />
                                    </div>
                                </Link>
                            ))}
                    </div>
                </div>
            </section>

            {/* ---------- WHY MABS ---------- */}
            <section
                className="bg-ink-deep py-20 md:py-28 border-y border-ink-border"
                data-testid="home-why-mabs"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10">
                    <div className="max-w-2xl mb-14">
                        <div className="eyebrow mb-3">
                            {pick(
                                { fr: "Notre approche", en: "Our approach" },
                                lang,
                            )}
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight text-neutral-50">
                            {pick(UI_STRINGS.sections.why_mabs, lang)}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {(home.why_mabs || []).map((item, idx) => (
                            <div
                                key={idx}
                                className="card-line p-6"
                                data-testid={`why-mabs-item-${idx}`}
                            >
                                <div className="mb-4">
                                    <CmsIcon name={item.icon} size={24} />
                                </div>
                                <h3 className="font-serif text-lg text-neutral-50 mb-3 leading-snug">
                                    {pick(item.title, lang)}
                                </h3>
                                <p className="text-sm text-neutral-400 leading-relaxed">
                                    {pick(item.text, lang)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- QUICK CONTACT ---------- */}
            <section
                id="form"
                className="bg-ink py-20 md:py-28"
                data-testid="home-contact"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-14">
                    <div>
                        <div className="eyebrow mb-3">
                            {pick(UI_STRINGS.sections.contact_quick, lang)}
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight text-neutral-50 mb-5">
                            {pick(
                                {
                                    fr: "Écrivez au cabinet en quelques mots",
                                    en: "Write to the firm in a few words",
                                },
                                lang,
                            )}
                        </h2>
                        <p className="text-neutral-300 leading-relaxed max-w-lg">
                            {pick(UI_STRINGS.sections.contact_quick_sub, lang)}
                        </p>

                        {contactInfo && (
                            <div className="mt-10 space-y-4 border-l border-ink-border pl-6">
                                <div className="flex items-start gap-3 text-sm text-neutral-300">
                                    <MapPin
                                        size={16}
                                        className="text-gold mt-1"
                                    />
                                    <span>{pick(contactInfo.address, lang)}</span>
                                </div>
                                <div className="flex items-start gap-3 text-sm text-neutral-300">
                                    <Phone
                                        size={16}
                                        className="text-gold mt-1"
                                    />
                                    <div className="flex flex-col gap-1">
                                        {contactInfo.phones.map((p) => (
                                            <a
                                                key={p}
                                                href={`tel:${p.replace(/\s+/g, "")}`}
                                                className="hover:text-gold transition-colors"
                                            >
                                                {p}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                                <a
                                    href={whatsappHref(
                                        contactInfo.whatsapp_local,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-whatsapp mt-4"
                                    data-testid="home-whatsapp-local"
                                >
                                    <MessageCircle
                                        size={18}
                                        className="wa-icon"
                                    />
                                    WhatsApp · {contactInfo.whatsapp_local}
                                </a>
                            </div>
                        )}
                    </div>
                    <div className="card-line p-6 md:p-10">
                        <QuickContactForm />
                    </div>
                </div>
            </section>
        </div>
    );
}
